import type { FC } from 'react';
import styled, { css, withTheme } from 'styled-components';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { Routes } from '~/common/constants/routes';
import { regionInfoState, regionModalOpenState } from '~/recoil/useRegion';
import CountryFlag from '~/components/CountryFlag';
import { useNavigation } from '~/common/hooks/useNavigation';
import { useRegionsEnabled } from '~/common/hooks/useRegionsEnabled';
import { Badge } from '~/components/badge';
import { NavigationItem, NavigationRow } from '~/layout/styles';
import { Theme } from '~/theme';
import { colors } from '~/theme/colors';
import { fontFamilies } from '~/theme/typography';
import MobileSidebar from '../sidebar/Mobile';
import useIsMobileNavigation from '~/common/hooks/useIsMobileNavigation';
import type { Region } from '~/common/constants/region';
import { getStoreLabel, toStore } from '~/common/utils/region';
import BagOutline from 'public/assets/icons/bag_outline.svg';

interface HeaderProps {
  showLogo: boolean;
  cartItemCount: number;
  currentRegion: Region;
  theme?: Theme;
  isMobileNavigationOpen?: boolean;
  toggleMobileNavigation: (val: boolean) => void;
}

const Header: FC<HeaderProps> = withTheme(
  ({
    showLogo,
    cartItemCount,
    currentRegion,
    isMobileNavigationOpen,
    toggleMobileNavigation,
  }: HeaderProps) => {
    const { goToHome, goTo } = useNavigation();
    const showMobile = useIsMobileNavigation();
    const setRegionModalOpen = useSetRecoilState(regionModalOpenState);
    const regionsEnabled = useRegionsEnabled();
    const { countryCode } = useRecoilValue(regionInfoState);
    const store = toStore(currentRegion, countryCode);

    return (
      <header>
        <StyledHeader $centerLogo={showMobile}>
          {showMobile && (
            <MobileSidebar
              isMobileNavigationOpen={isMobileNavigationOpen}
              toggleMobileNavigation={toggleMobileNavigation}
            />
          )}
          <LogoWrapper onClick={goToHome}>
            {showLogo && (
              <Logo
                src="/assets/icons/header-logo.svg"
                alt="Logo of broken planet"
                loading="eager"
                width={208}
                height={24}
              />
            )}
          </LogoWrapper>
          <RightSection>
            {regionsEnabled && (
              <>
                <DesktopOnly>
                  <RegionLabelButton
                    type="button"
                    aria-label={`Store: ${store.name}. Click to change.`}
                    onClick={() => setRegionModalOpen(true)}
                  >
                    <CountryFlag code={countryCode} />
                    <RegionLabelText>{getStoreLabel(store)}</RegionLabelText>
                  </RegionLabelButton>
                </DesktopOnly>
                <MobileOnly>
                  <RegionIconButton
                    type="button"
                    aria-label="Select store"
                    onClick={() => setRegionModalOpen(true)}
                  >
                    <CountryFlag code={countryCode} />
                  </RegionIconButton>
                </MobileOnly>
              </>
            )}
            <Badge
              count={cartItemCount}
              $backgroundColor={colors.greyDisabled2}
            >
              <StyledNavigationItem
                to={Routes.Cart}
                onClick={() => goTo(Routes.Cart)}
                aria-label="Bag"
              >
                <BagText>BAG</BagText>
                <BagIcon src={BagOutline} alt="" width={24} height={24} />
              </StyledNavigationItem>
            </Badge>
          </RightSection>
        </StyledHeader>
      </header>
    );
  },
) as FC<HeaderProps>;

const LogoWrapper = styled.button`
  background-color: transparent;
  border: none;
  box-shadow: none;
  padding: 0;
  height: 1.5rem;

  @media ${({ theme }) => theme.breakpoints.sm} {
    height: 1.009rem;
  }
`;

const Logo = styled.img`
  cursor: pointer;

  @media ${({ theme }) => theme.breakpoints.sm} {
    width: 8.75rem;
    height: 1.009rem;
  }
`;

// The burger only renders in mobile navigation, so the row has three children
// there and two on desktop. `NavigationRow`'s `space-between` puts the logo's
// centre at (left - right) / 2 off true centre, which the hand-tuned margins on
// the burger and the badge used to paper over — a balance that depended on the
// UA's default `<button>` padding and broke whenever an icon was added, removed
// or resized. A symmetric grid centres it whatever the side clusters weigh.
//
// `minmax(0, 1fr)`, not `1fr`: the latter is `minmax(auto, 1fr)`, whose
// min-content floor lets the side columns go unequal when cramped, which is the
// same bug again. Keyed on the `showMobile` that renders the burger rather than
// on `breakpoints.lg`, because /sizing-reference keeps the burger up to 1380px.
//
// The track count is tied to the child count: a fourth child would land in an
// implicit column and knock the logo off centre. Add icons inside RightSection.
const StyledHeader = styled(NavigationRow)<{ $centerLogo: boolean }>`
  padding: 1.5rem 1.5rem 0;
  top: var(--rolling-header-offset, 0);
  height: auto;
  transition: box-shadow 0.15s ease-in-out;

  ${({ $centerLogo }) =>
    $centerLogo &&
    css`
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    `}

  @media ${({ theme }) => theme.breakpoints.sm} {
    padding: 1rem 1rem 0;
  }
`;

export const StyledNavigationItem = styled(NavigationItem)`
  @media ${({ theme }) => theme.breakpoints.md} {
    font-size: 0.875rem;
    height: 24px;
  }
`;

const DesktopOnly = styled.div`
  display: contents;

  @media ${({ theme }) => theme.breakpoints.lg} {
    display: none;
  }
`;

const MobileOnly = styled.div`
  display: none;

  @media ${({ theme }) => theme.breakpoints.lg} {
    display: contents;
  }
`;

const RegionIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;

  > img {
    width: 24px;
    height: 24px;
  }
`;

// Trims the line box to the capitals, so a row of labels centres on the letters
// rather than on the font's ascent/descent, which PantonRust reports differently
// per OS. Every text label in RightSection needs it, or they drift apart.
// Unsupported browsers (Firefox) keep the untrimmed box.
const capTrim = css`
  text-box: trim-both cap alphabetic;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  /* Honoured in the mobile grid, ignored while the row is flex on desktop. */
  justify-self: end;

  @media ${({ theme }) => theme.breakpoints.lg} {
    gap: 1rem;
  }
`;

// Block, so the link's own line box (strut in another font) can't offset it.
const BagText = styled.span`
  ${capTrim}
  display: block;
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.PantonRustHeavy};
  font-size: 1.5rem;
  font-style: normal;
  font-weight: 100;
  line-height: normal;
  letter-spacing: 0.0075rem;
  text-transform: uppercase;

  @media ${({ theme }) => theme.breakpoints.lg} {
    display: none;
  }
`;

const BagIcon = styled.img`
  display: none;
  width: 24px;
  height: 24px;

  @media ${({ theme }) => theme.breakpoints.lg} {
    display: block;
  }
`;

export default Header;

const RegionLabelButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.PantonRustHeavy};
  font-size: 1.5rem;
  font-weight: 100;
  letter-spacing: 0.0075rem;
  text-transform: uppercase;
  transition: opacity ${({ theme }) => theme.transitions.button};

  &:hover {
    opacity: 0.7;
  }
`;

// `font-family: inherit` overrides the global `*` CircularStd rule, which a
// bare span would pick up.
const RegionLabelText = styled.span`
  font-family: inherit;
  ${capTrim}
`;
